"use client";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card.tsx";
import { Button } from "../../../components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/dropdown-menu.tsx";
import { MoreVertical, FileDown, Edit } from "lucide-react";
import { Order } from "../types/order.ts";
import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/pwaConfig.ts";
import { QuotationTemplate, QuotationTermsTemplate, CombinedQuotationTemplate } from "./QuotationTemplate.tsx";
import { QuotationTemplate as QuotationTemplatePrint } from "./QuotationTemplate_ForPrint.tsx";
import { downloadMergedPDF, downloadPDF } from "../../../utils/pdfGenerator.ts";
import { getOrderDeliveryText } from "../orders/orderDeliveryText";

// Helper function to format date/time to India timezone
const formatIndiaTime = (dateString: string | Date | undefined) => {
  if (!dateString || dateString === '') return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata'
  }).replace(',', '');
};

// Helper function to format date only (without time) to India timezone
const formatDateOnly = (dateString: string | Date | undefined) => {
  if (!dateString || dateString === '') return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  }).replace(/\//g, '-');
};
interface Props {
  order: Order;
  refresh: () => void;
  onEdit: (order: Order) => void;
  orderStatuses?: { id: number; label: string; color: string }[];
}

export const OrderCard: React.FC<Props> = ({ order, refresh, onEdit, orderStatuses = [] }) => {
  const [showOrderDetails,setShowOrderDetails]=useState<Order | null>(null)
  const [previewColor, setPreviewColor] = useState<any>(null);
  const { authToken = "", id = "", role="", name="", username="" } = useSelector(
    (state: any) => state.loginData || {}
  );
  const handleStatusChange = async (status: string) => {
    const payload = {
      ...order,
      status,
      createdBy: username,
      revisedQuotation: false,
    };
    try {
      await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      refresh();
    } catch (e) {
      console.error("Error updating order status:", e);
    }
  };
const handleDownloadPDF = async () => {
  try {
    await downloadPDF(
      <CombinedQuotationTemplate order={order} />,
      `Quotation_${order.customerName || "Order"}.pdf`
    );
  } catch (error) {
    console.error("Error downloading PDF:", error);
    alert("Failed to download PDF");
  }
};

const handleDownloadPrintFormatPDF = async () => {
  try {
    await downloadMergedPDF(
      <QuotationTemplatePrint order={order} />,
      <QuotationTermsTemplate order={order} />,
      `PrintFormat_${order.customerName || "Order"}.pdf`
    );
  } catch (error) {
    console.error("Error downloading Print Format PDF:", error);
    alert("Failed to download Print Format PDF");
  }
};
   const handleSubmit = async () => {
    const payload = {
      ...order,
      createdBy: username,
      invoiceGenerated: false,
      revisedQuotation: false,
    };
  
    try {
        await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
     
    } catch (e) {
      console.error("Error saving order:", e);
    }
  
    refresh();
  };
  

  return (
    <Card
      className="p-0 border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-[var(--accent-primary)] transition-all group cursor-pointer"
      onClick={() => setShowOrderDetails(order)}
    >
      <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 flex justify-between items-start p-5 border-b-2 border-gray-200">
        <div className="flex-1">
          <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-orange-700 transition-colors">
            📄 {order.customerName}
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1 font-medium">
            Invoice ID: <span className="text-orange-600 font-semibold">{order.invoiceId}</span>
          </p>
          
        </div>
        <div
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2">
            {(() => {
              const matched = orderStatuses.find(
                (s) => s.label?.toLowerCase() === order.status?.toLowerCase()
              );
              const bgColor = matched?.color ?? "#EAB308";
              const label = matched?.label ?? (order.status || "New");
              return (
                <span
                  style={{ backgroundColor: bgColor + "22", color: bgColor, border: `1.5px solid ${bgColor}` }}
                  className="px-3 py-1 rounded-full text-xs font-bold"
                >
                  {label}
                </span>
              );
            })()}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-white">
                <MoreVertical className="w-4 h-4 text-gray-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white rounded-lg shadow-lg min-w-[200px]">
              {orderStatuses.map((status) => (
                <DropdownMenuItem
                  key={status.id}
                  onClick={() => handleStatusChange(status.label)}
                  className="cursor-pointer text-gray-700 flex items-center gap-2"
                >
                  <span
                    style={{ backgroundColor: status.color }}
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  />
                  {status.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => handleDownloadPrintFormatPDF()}
                className="cursor-pointer hover:bg-green-50 text-gray-700"
              >
                Download Print Format
              </DropdownMenuItem>
              {role === "admin" && (
                <DropdownMenuItem
                  onClick={() => handleSubmit()}
                  className="cursor-pointer hover:bg-purple-50 text-gray-700"
                >
                  Cancel Invoice
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 font-semibold mb-1">Created By</p>
            <p className="text-sm font-bold text-gray-900">{order.createdBy || "N/A"}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 font-semibold mb-1">Created At</p>
            <p className="text-sm font-bold text-gray-900">
              {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}
            </p>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-xs text-gray-500 font-semibold mb-1">Total Amount</p>
          <p className="text-lg font-bold text-orange-700">₹{order.total ? parseFloat(order.total.toString()).toFixed(2) : "0.00"}</p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleDownloadPDF(); }}
            className="flex-1 md:flex-none border-green-300 text-green-600 hover:bg-green-50 font-semibold"
          >
            <FileDown className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        </div>
      </CardContent>

      {/* Inline Order Details */}
      {showOrderDetails && (
        <div className="border-t-2 border-[var(--accent-primary)]/30 bg-white">
          <div className="px-5 py-3 bg-[var(--sidebar-light)] flex justify-between items-center border-b border-[var(--accent-primary)]/30">
            <h2 className="text-base font-bold text-gray-900">
              Order Details — {order.customerName}
            </h2>
            <button
              onClick={() => setShowOrderDetails(null)}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ✕
            </button>
          </div>

          <div className="p-5 space-y-4">

          {/* Order Details in Single Box */}
          <div className="bg-white border-2 border-gray-300 rounded-xl p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Row 1 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Order Number</p>
                <p className="text-base font-bold text-gray-900 break-words">{order.id}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Order Date & Time</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(order.createdAt)}</p>
              </div>

              {/* Row 2 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Customer Name</p>
                <p className="text-base font-bold text-gray-900 break-words">{order.customerName}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Customer ID</p>
                <p className="text-base font-bold text-gray-900 break-words">{order.customerId}</p>
              </div>

              {/* Row 3 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Created On</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(order.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Updated Date</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(order.updatedAt)}</p>
              </div>

              {/* Row 4 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Delivery</p>
                <p className="text-base font-bold text-gray-900 break-words">{getOrderDeliveryText(order) || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Delivered By Company</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatDateOnly(order.toBeDelivered)}</p>
              </div>
            </div>
          </div>

          {/* Price Summary Table */}
       {order.quotation && (
  <div className="bg-white border-2 border-[var(--accent-primary)]/30 rounded-xl overflow-x-auto">
    <div className="w-full">
      {(() => {
        const hasFixing = parseFloat(order.fixing || "0") > 0;
        const hasTransport = parseFloat(order.transport || "0") > 0;
        const hasDiscount = parseFloat(order.discount || "0") > 0;

        const colCount =
          3 +
          (hasFixing ? 1 : 0) +
          (hasTransport ? 1 : 0) +
          (hasDiscount ? 1 : 0);

        // 🔴 Tailwind-safe dynamic columns
        const gridStyle = {
          gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
        };

        return (
          <>
            {/* Header Row */}
           <div
  className="grid w-full box-border gap-0 bg-gradient-to-r from-[var(--sidebar-primary)] to-[var(--accent-primary)] text-white font-bold text-sm"
  style={gridStyle}
>
              <div className="p-3 text-center border-r border-[var(--accent-primary)]/50">
                Total
              </div>
              <div className="p-3 text-center border-r border-[var(--accent-primary)]/50">
                GST
              </div>
              {hasFixing && (
                <div className="p-3 text-center border-r border-[var(--accent-primary)]/50">
                  Fixing
                </div>
              )}
              {hasTransport && (
                <div className="p-3 text-center border-r border-[var(--accent-primary)]/50">
                  Transport
                </div>
              )}
              {hasDiscount && (
                <div className="p-3 text-center border-r border-[var(--accent-primary)]/50">
                  Discount
                </div>
              )}
              <div className="p-3 text-center">Total Amount</div>
            </div>

            {/* Data Row */}
           <div
  className="grid w-full box-border gap-0 bg-[var(--sidebar-light)]/50"
  style={gridStyle}
>
              <div className="p-3 text-center border-r border-[var(--accent-primary)]/20 overflow-hidden">
                <p className="text-xs font-bold text-gray-900 break-words">
                  ₹{order.price
                    ? parseFloat(order.price.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>

              <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                <p className="text-xs font-bold text-gray-900 break-words">
                  ₹{order.gst
                    ? parseFloat(order.gst.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>

              {hasFixing && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(order.fixing ? order.fixing.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}

              {hasTransport && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(order.transport ? order.transport.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}

              {hasDiscount && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(order.discount ? order.discount.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}
              <div className="p-3 text-center bg-green-100 overflow-hidden">
                <p className="text-xs font-bold text-green-700 break-words">
                  ₹{order.total
                    ? parseFloat(order.total.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  </div>
)}


          {/* Notes */}
          {order.notes && (
            <div>
              <p className="text-xs text-gray-500 font-semibold mb-2">Notes:</p>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 break-all whitespace-pre-wrap overflow-hidden">
                <p className="text-sm font-bold text-gray-900">{order.notes}</p>
              </div>
            </div>
          )}

          {/* Products Section */}
          {order.products && order.products.length > 0 && (
            <div className="border-t pt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4">🛍️ Products</h3>
              <div className="space-y-4">
                {order.products.map((product: any, index: number) => (
                  <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 space-y-3">
                    {/* 1. Product Name */}
                    <div className="bg-white rounded-lg p-3 border border-green-300">
                      <p className="text-xs text-gray-600 font-bold uppercase mb-1">Product Name</p>
                      <p className="text-lg font-bold text-green-700">{product.name}</p>
                    </div>

                    {/* 2. Specifications */}
                    {product.specifications && Object.keys(product.specifications).length > 0 && (
                      <div className="bg-white border-2 border-yellow-300 rounded-lg p-4">
                        <p className="text-xs text-gray-600 font-bold uppercase mb-3">Specifications</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {Object.entries(product.specifications).map(([key, value]: [string, any]) => (
                            <div key={key}>
                              <p className="text-xs text-gray-600 font-bold capitalize mb-1">{key.replace(/_/g, " ")}</p>
                              {typeof value === "object" && value !== null && value.image ? (
                                <div className="flex items-center gap-2">
                                  <img
                                    src={value.image}
                                    alt={value.name}
                                    className="w-6 h-6 rounded border border-yellow-300 object-cover cursor-pointer hover:shadow-lg hover:scale-110 transition-all"
                                    onClick={() => setPreviewColor(value)}
                                  />
                                  <p className="text-sm font-bold text-yellow-700 break-words">{value.name || "N/A"}</p>
                                </div>
                              ) : typeof value === "object" && value !== null && value.name ? (
                                <p className="text-sm font-bold text-yellow-700 break-words">{value.name || "N/A"}</p>
                              ) : (
                                <p className="text-sm font-bold text-yellow-700 break-words">{value || "N/A"}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 3. Place, Width, Height, Quantity, Area - All in Single Box */}
                    <div className="bg-white border-2 border-blue-300 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Place</p>
                          <p className="text-sm font-bold text-teal-700 break-words">{product.place || "N/A"}</p>
                        </div>
                        {product.width != null && product.width !== "0" && product.width !== 0 && <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Width</p>
                          <p className="text-sm font-bold text-blue-700 break-words">
                            {product.width || "N/A"}<sup>{product.nool_width || ""}</sup>
                            {product.width_uom && <span className="text-gray-500 ml-1">{product.width_uom}</span>}
                          </p>
                        </div>}
                      {product.height != null && product.height !== "0" && product.height !== 0 && <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Height</p>
                          <p className="text-sm font-bold text-cyan-700 break-words">
                            {product.height || "N/A"}<sup>{product.nool_height || ""}</sup>
                            {product.height_uom && <span className="text-gray-500 ml-1">{product.height_uom}</span>}
                          </p>
                        </div>}
                         {product.length != null && product.length !== "0" && product.length !== 0 && <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Length</p>
                          <p className="text-sm font-bold text-cyan-700 break-words">
                            {product.length || "N/A"}
                            {product.length_uom && <span className="text-gray-500 ml-1">{product.length_uom}</span>}
                          </p>
                        </div>}
                        <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Quantity</p>
                          <p className="text-sm font-bold text-red-700 break-words">{product.nos || "N/A"}</p>
                        </div>
                        {order.quotation && (
                          <div>
                            <p className="text-xs text-gray-600 font-bold mb-1">Area</p>
                            <p className="text-sm font-bold text-indigo-700 break-words">{product.area ? parseFloat(product.area.toString()).toFixed(2) : "N/A"} {product.area_uom || ""}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 4. Price and GST - Single Box */}
                    {order.quotation && (
                      <div className="bg-white border-2 border-purple-300 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <p className="text-xs text-gray-600 font-bold mb-1">Price</p>
                            <p className="text-sm font-bold text-pink-700 break-words">₹{product.price ? parseFloat(product.price.toString()).toFixed(2) : "0.00"}</p>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-xs text-gray-600 font-bold mb-1">GST</p>
                            <p className="text-sm font-bold text-orange-700 break-words">{product.gst || "0"}%</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Image Preview Drawer */}
      {previewColor && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4" onClick={() => setPreviewColor(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              onClick={() => setPreviewColor(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>

            {/* Enlarged Image */}
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-pink-300 shadow-md flex items-center justify-center bg-gray-50">
              {previewColor?.image ? (
                <img
                  src={previewColor.image}
                  alt={previewColor.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  <span className="text-gray-500 text-sm">No image available</span>
                </div>
              )}
            </div>

            {/* Color Name */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewColor.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Color</p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
